import React, { useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import AnimatedButton from "../components/AnimatedButton";

const AboutUs = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      title: "Housekeeping & Janitorial",
      desc: "Comprehensive cleaning solutions for offices, retail spaces, and residential properties.",
      icon: "🧹",
    },
    {
      title: "Industrial Cleaning",
      desc: "Specialized services for factories, warehouses, and manufacturing plants.",
      icon: "🏭",
    },
    {
      title: "Hospitality Support",
      desc: "Premium cleaning for hotels, resorts, and event venues.",
      icon: "🏨",
    },
    {
      title: "Security Services",
      desc: "Trained personnel for 24/7 facility protection and monitoring.",
      icon: "🛡️",
    },
    {
      title: "Maintenance Solutions",
      desc: "Complete technical support for all facility maintenance needs.",
      icon: "🔧",
    },
    {
      title: "Pest Control",
      desc: "Eco-friendly pest management for all environments.",
      icon: "🐜",
    },
  ];

  const stats = [
    { value: "200+", label: "Satisfied Clients" },
    { value: "500+", label: "Trained Professionals" },
    { value: "24/7", label: "Support Availability" },
    { value: "98%", label: "Client Retention" },
  ];

  return (
    <div className="about-us-page">
      {/* Hero Banner with Image */}
      <section className="about-banner position-relative overflow-hidden">
        <div className="container position-relative z-index-1">
          <div className="row align-items-center">
            <div className="col-lg-12 text-center">
              <h1 className="display-5 fw-bold mb-4">
                Redefining Facility Management
              </h1>
              <p>
                At RR Facility Services, we combine cutting-edge technology with
                human expertise to deliver unparalleled facility solutions
                across industries.
              </p>
              <a href="/contact">
                <AnimatedButton text="Get in Touch" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Company Intro */}
      <section className="whoweare py-5 bg-white">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-5 mb-lg-0 animate-on-scroll">
              <div className="about-image-wrapper position-relative">
                <img
                  src="https://images.unsplash.com/photo-1521791136064-7986c2920216"
                  alt="Our team at work"
                  className="img-fluid rounded-3"
                />
              </div>
            </div>
            <div className="col-lg-6 animate-on-scroll">
              <h2 className="fw-bold">Who We Are</h2>
              <p className="mb-2">
                Founded in 2008, RR Facility Services has grown from a local
                cleaning company to a comprehensive facility management provider
                serving clients nationwide.
              </p>
              <p className="mb-4">
                Our journey began with a simple mission: to deliver cleaning
                services that exceed expectations. Today, we offer a full
                spectrum of facility solutions while maintaining our commitment
                to quality, reliability, and innovation.
              </p>
              <ul className="list-unstyled mb-4">
                <li className="mb-2 d-flex">
                  <span className="me-2 text-primary">✓</span>
                  <span>ISO 9001 Certified Operations</span>
                </li>
                <li className="mb-2 d-flex">
                  <span className="me-2 text-primary">✓</span>
                  <span>Green Cleaning Certified</span>
                </li>
                <li className="mb-2 d-flex">
                  <span className="me-2 text-primary">✓</span>
                  <span>Women-Owned Business Enterprise</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row g-4">
            {stats.map((stat, index) => (
              <div className="col-md-3 col-6 animate-on-scroll" key={index}>
                <div className="stat-card text-center p-4 bg-white rounded-3 shadow-sm">
                  <div className="display-4 fw-bold text-primary mb-2">
                    {stat.value}
                  </div>
                  <div className="text-muted">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">Our Comprehensive Services</h2>
            <p className="mx-auto">
              Customized solutions designed for your specific facility
              requirements
            </p>
          </div>

          <div className="row g-4">
            {services.map((service, index) => (
              <div className="col-md-6 col-lg-4 animate-on-scroll" key={index}>
                <div className="service-card h-100 p-4 rounded-3 shadow-hover">
                  <div className="service-icon mb-3">
                    <span className="display-6">{service.icon}</span>
                  </div>
                  <h3 className="h4 fw-bold mb-3">{service.title}</h3>
                  <p className="text-muted mb-0">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 bg-light">
        <div className="container text-center">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <h2 className="fw-bold display-5 mb-2">
                Ready to Elevate Your Facility Standards?
              </h2>
              <p className="lead mb-3 opacity-75">
                Partner with RR Facility Services for comprehensive, reliable
                facility solutions tailored to your unique needs.
              </p>
              <a href="/contact">
                <AnimatedButton text="Get Quote" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
